import Flex from 'antd/es/flex';
import Table, { TableProps } from 'antd/es/table';
import { ColumnsType, ColumnType, SortOrder } from 'antd/es/table/interface';
import classNames from 'classnames';

import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { SortConfig } from '@Features/types/SortConfig';

import MPagination from '@Components/Base/Table/components/Pagination';
import { User } from '@Types/index';
import { AnyObject } from 'antd/es/_util/type';
import { ReactElement, ReactNode, useMemo, useRef } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useResizeObserver } from 'usehooks-ts';
import Text from 'antd/es/typography/Text';
import {
  mapToCustomPageIndex,
  mapToDefaultPageIndex,
  prepareSortConfig,
} from '../../../../../../components/Base/Table/utils';
import {
  MaterialDto,
  PriceComparisonDto,
  PriceSelectionItemDto,
  TenderSupplier,
  VendorOfferDto,
} from '../ComparisonSheet.types';
import { MaterialRequestInfo } from '../MaterialRequestInfo';
import styles from './PriceSelection.module.css';
import { withSortIcon } from '../utils';

const TABLE_HEADER_HEIGHT = 63;

// such config is needed to exclude 'Click to cancel sorting' option
const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend', 'ascend'];

/**
 * @todo rename dataSource to comparison table item def
 */
export interface MTableProps<RecordType = PriceComparisonDto>
  extends Omit<TableProps<RecordType>, 'columns'> {
  /**
   * @description replaces columns definition
   */
  vendors: Array<TenderSupplier>;
  // pagination @todo
  totalItems?: number;
  pageSize?: number;
  onChangePage?: (page: number) => void;
  onSort?: (sortConfig: SortConfig | null) => void;
  currentPage?: number;
  hidePagination?: boolean;
}

type MTable2ComponentType<T1 = TenderSupplier> = React.FC<MTableProps<T1>>;

const quantityFormatter = new Intl.NumberFormat('ae-AE', {
  style: 'decimal',
  minimumFractionDigits: 3,
});

const renderAmountColumn = (value: number): ReactNode => {
  return value && quantityFormatter.format(value);
};

const renderAvailColumn = (value: boolean): ReactNode => {
  return (value && 'Yes') || 'No';
};

// TODO: add or remove after BA confirm
// const renderApproverColumn = (value: Partial<User>): ReactNode => {
//   return <Flex>{value.empName}</Flex>;
// };

const RenderPaddings = ({
  row,
  children,
}: {
  row: TPriceSelectionRow;
  children: ReactNode;
}): ReactElement => {
  return (
    <div
      className={classNames(
        row.isFirst && !row.isLast ? styles['cell-top'] : null,
        row.isLast && !row.isFirst ? styles['cell-bottom'] : null
      )}
    >
      {children}
    </div>
  );
};

type TPriceSelectionRow = {
  id?: number | string;
  material: MaterialDto | null;
  approver?: Partial<User>;
} & VendorOfferDto & {
    rowSpan: number | null;
    isFirst: boolean;
    isLast: boolean;
  };

const toPriceSelectionRows = (
  priceDto: readonly PriceSelectionItemDto[]
): TPriceSelectionRow[] => {
  return priceDto
    .map(({ vendorOffers: offers, ...pd }) => {
      return offers.map((offer, i) => ({
        ...pd,
        ...offer,
        material: i === 0 ? pd.material : null,
        rowSpan: i === 0 ? offers.length : null,
        isFirst: i === 0,
        isLast: i === offers.length - 1,
      }));
    })
    .flat();
};

const PriceSelection = (
  props: MTableProps<PriceSelectionItemDto>
): ReturnType<MTable2ComponentType<TPriceSelectionRow>> => {
  const {
    dataSource,
    totalItems,
    pageSize,
    onChangePage,
    onSort,
    currentPage,
    className,
    hidePagination = false,
    vendors,
    ...restProps
  } = props;

  const data = toPriceSelectionRows(dataSource || []);

  const currentPageValue = mapToCustomPageIndex(
    currentPage || TABLE_PAGE_DEFAULT
  );

  const containerRef = useRef<HTMLElement | null>(null);
  const { height = 0, width = 0 } = useResizeObserver({
    ref: containerRef,
    box: 'border-box',
  });

  const columns: ColumnsType<TPriceSelectionRow> = useMemo(() => {
    return (
      [
        {
          title: 'Materials',
          dataIndex: ['material'],
          key: 'name',
          width: 370,
          fixed: 'left',
          render: (value) =>
            value && (
              <>
                <MaterialRequestInfo value={value} />
                <div className={styles.after} />
              </>
            ),
          onHeaderCell: () => {
            return {
              className: classNames(styles['material-cell-header']),
            };
          },
          onCell: (record) =>
            record.rowSpan
              ? {
                  rowSpan: record.rowSpan,
                  className: classNames(
                    styles['valign-top'],
                    styles['material-cell']
                  ),
                }
              : {
                  rowSpan: 0,
                  className: classNames(
                    styles['valign-top'],
                    styles['material-cell']
                  ),
                },
        } satisfies ColumnType<TPriceSelectionRow>,
        {
          title: 'Vendor Name',
          dataIndex: ['vendorId'],
          key: ['vendorId'].join('_'),
          ellipsis: false,
          render: (value: number, row) => (
            <RenderPaddings row={row}>
              <Text className="link">
                {vendors.find((s) => String(s.id) === String(value))?.name}
              </Text>
            </RenderPaddings>
          ),
          sorter: (a, b) => {
            // eslint-disable-next-line no-nested-ternary
            return a > b ? 1 : a < b ? -1 : 0;
          },
        },
        {
          title: 'Rate',
          dataIndex: ['rate'],
          key: ['rate'].join('_'),
          width: 120,
          render: (value: number, row) => (
            <RenderPaddings row={row}>
              {renderAmountColumn(value)}
            </RenderPaddings>
          ),
          sorter: (a, b) => {
            // eslint-disable-next-line no-nested-ternary
            return a > b ? 1 : a < b ? -1 : 0;
          },
          onCell: () => ({
            className: classNames(styles['info-cell']),
          }),
        },
        {
          title: 'Amount',
          dataIndex: ['amount'],
          key: ['amount'].join('_'),
          width: 120,
          render: (value: number, row) => (
            <RenderPaddings row={row}>
              {renderAmountColumn(value)}
            </RenderPaddings>
          ),
          sorter: true,
        },
        {
          title: 'Available',
          dataIndex: ['available'],
          key: ['available'].join('_'),
          width: 150,
          render: (value: boolean, row) => (
            <RenderPaddings row={row}>
              {renderAvailColumn(value)}
            </RenderPaddings>
          ),
          sorter: true,
        },
        {
          title: 'Brand',
          dataIndex: ['brand'],
          key: ['brand'].join('_'),
          width: 170,
          render: (value: string, row) => (
            <RenderPaddings row={row}>{value}</RenderPaddings>
          ),
          sorter: true,
        },
        {
          title: 'Quantity',
          dataIndex: ['vendorQty'],
          key: ['vendorQty'].join('_'),
          width: 170,
          render: (value: string, row) => (
            <RenderPaddings row={row}>{value}</RenderPaddings>
          ),
          sorter: true,
        },
        // TODO: add or remove after BA confirm
        // {
        //   title: 'Approved By',
        //   dataIndex: ['approver'],
        //   key: ['approver'].join('_'),
        //   width: 170,
        //   render: (value: Partial<User>, row) => (
        //     <RenderPaddings row={row}>
        //       {renderApproverColumn(value)}
        //     </RenderPaddings>
        //   ),
        // },
      ] satisfies ColumnsType<TPriceSelectionRow>
    ).map((c) => withSortIcon(c));
  }, [vendors]);

  const handleChange = (page: number): void => {
    const pageIndex = mapToDefaultPageIndex(page);
    onChangePage?.(pageIndex);
  };

  return (
    <Flex
      vertical
      className={classNames(
        styles['price-selection'],
        'trojan-table-container'
      )}
      ref={containerRef}
      gap={16}
    >
      <Table<TPriceSelectionRow>
        {...(restProps as unknown as AnyObject)}
        className={classNames('trojan-table', className, 'table-flex')}
        sortDirections={SORT_DIRECTIONS}
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: width, y: height - TABLE_HEADER_HEIGHT }}
        onChange={(_pagination, _filters, sorter, extra) => {
          if (extra.action === 'sort') {
            const sortConfig = prepareSortConfig(sorter);
            onSort?.(sortConfig);
          }
        }}
      />
      {!hidePagination && columns && data && (
        <Flex className="trojan-table-pagination trojan-box-shadow trojan-table-pagination-box-shadow">
          <MPagination
            showQuickJumper
            pageSize={pageSize}
            showSizeChanger={false}
            current={currentPageValue}
            total={totalItems}
            onChange={handleChange}
            totalItems={totalItems}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default PriceSelection;
